import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../context/AuthContext';
import {
  Wrench, Package, Users, MapPin, Plus, Gift, Clock,
  Navigation, ArrowRight, User, ChevronDown, ChevronUp,
  FolderOpen, Tag, Search, X, Loader2
} from 'lucide-react';
import BorrowModal from '../components/BorrowModal';
import ClaimRequestModal from '../components/ClaimRequestModal';
import ItemFormModal from '../components/ItemFormModal';
import EntityTile from '../components/EntityTile';
import ProjectFormModal from '../components/ProjectFormModal';
import CategoryFilterDropdown from '../components/CategoryFilterDropdown';
import StatusProgress from '../components/StatusProgress';
import ToolCard from '../components/ToolCard';
import SmartImg from '../components/SmartImg';
import OnboardingModal from '../components/OnboardingModal';
import { thumbFrom } from '../services/imageUpload';
import { toast } from 'sonner';

// ─── LCP preload helper ─────────────────────────────────────────────────────
// Injects `<link rel="preload" as="image">` for the hero photo of each visible
// tab as soon as the feed API returns — this happens *before* React commits
// the feed tree, so the browser starts fetching the LCP image in parallel
// with React's render pass instead of after it. Idempotent: a Set guards
// against re-preloading the same URL if the user navigates away and back.
//
// IMPORTANT: the rendered <img> uses the *thumb* URL (see feedImageProps),
// so we preload the thumb — not the full-size photo — so the browser matches
// the preload to the render and reuses the bytes.
const _preloadedHeroes = new Set();
function preloadHeroImages(feedItems) {
  if (!Array.isArray(feedItems) || typeof document === 'undefined') return;
  const heroFor = (type) => {
    const first = feedItems.find(i => i.type === type && i.photo);
    return first ? thumbFrom(first.photo) : null;
  };
  // Default tab is 'tools', so its hero IS the LCP. `fetchpriority=high`
  // on that one only — the others are warm-only for smooth tab switches.
  const targets = [
    ['item',     'high'],
    ['project',  'auto'],
    ['giveaway', 'auto'],
  ];
  for (const [type, priority] of targets) {
    const href = heroFor(type);
    if (!href || _preloadedHeroes.has(href)) continue;
    _preloadedHeroes.add(href);
    const link = document.createElement('link');
    link.rel = 'preload';
    link.as = 'image';
    link.href = href;
    // fetchpriority is honored by Chrome/Edge; Safari/FF ignore it (no harm).
    if (priority !== 'auto') link.setAttribute('fetchpriority', priority);
    document.head.appendChild(link);
  }
}

const formatTimeRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  const diff = new Date(expiresAt) - new Date();
  if (diff <= 0) return 'Expired';
  const days = Math.floor(diff / 86400000);  const hours = Math.floor((diff % 86400000) / 3600000);
  return days > 0 ? `${days}d left` : `${hours}h left`;
};

// Sort items: friends → community → public by distance (nulls last)
const sectionSort = (a, b) => {
  const rank = (i) => i.is_friend ? 0 : i.is_community ? 1 : 2;
  const ra = rank(a), rb = rank(b);
  if (ra !== rb) return ra - rb;
  // same tier: sort by distance ascending (null = very far)
  const da = a.distance ?? 9999, db = b.distance ?? 9999;
  return da - db;
};

const categoryColors = {
  'Power Tools': 'bg-orange-50 text-orange-700',
  'Hand Tools': 'bg-amber-50 text-amber-700',
  'Garden Tools': 'bg-green-50 text-green-700',
  'Ladders & Scaffolding': 'bg-yellow-50 text-yellow-700',
  'Painting Supplies': 'bg-indigo-50 text-indigo-700',
  'Plumbing': 'bg-blue-50 text-blue-700',
  'Electrical': 'bg-yellow-50 text-yellow-700',
  'Camping & Outdoor': 'bg-emerald-50 text-emerald-700',
  'Sports Equipment': 'bg-red-50 text-red-700',
  'Bikes & Scooters': 'bg-cyan-50 text-cyan-700',
  'Yard Games': 'bg-lime-50 text-lime-700',
  'Fishing & Hunting': 'bg-teal-50 text-teal-700',
  'Kitchen Appliances': 'bg-rose-50 text-rose-700',
  'Small Appliances': 'bg-pink-50 text-pink-700',
  'Party Supplies': 'bg-violet-50 text-violet-700',
  'Electronics': 'bg-purple-50 text-purple-700',
  'Cameras & AV': 'bg-indigo-50 text-indigo-700',
  'Gaming': 'bg-violet-50 text-violet-700',
  'Moving Equipment': 'bg-slate-50 text-slate-700',
  'Storage & Organization': 'bg-zinc-50 text-zinc-700',
  'Baby & Kids Gear': 'bg-sky-50 text-sky-700',
  'Toys & Games': 'bg-pink-50 text-pink-700',
  'Books & Media': 'bg-amber-50 text-amber-700',
  'Holiday Decorations': 'bg-red-50 text-red-700',
  'Seasonal Items': 'bg-orange-50 text-orange-700',
  'Cleaning Equipment': 'bg-blue-50 text-blue-700',
  'Safety Equipment': 'bg-yellow-50 text-yellow-700',
  'Musical Instruments': 'bg-purple-50 text-purple-700',
  'Furniture': 'bg-stone-100 text-stone-700',
  'Clothing & Accessories': 'bg-rose-50 text-rose-700',
  'Pet Supplies': 'bg-amber-50 text-amber-700',
  'Other': 'bg-stone-100 text-stone-600',
};

// ─── Card Components ──────────────────────────────────────────────────────────

function OwnerRow({ item }) {
  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500 font-nunito">
      <SmartImg
        src={item.owner_photo}
        alt={item.owner_name}
        className="w-4 h-4 rounded-full object-cover flex-shrink-0"
        fallbackClassName="w-4 h-4 rounded-full bg-stone-200 flex-shrink-0"
        fallback={<User size={8} className="text-stone-500" />}
      />
      <span className="truncate">{item.owner_name}</span>
      {item.distance !== null && item.distance !== undefined && (
        <span className="flex items-center gap-0.5 text-blue-600 flex-shrink-0">
          <MapPin size={9} />{item.distance}mi
        </span>
      )}
    </div>
  );
}

function SourceBadge({ item }) {
  if (item.is_friend) return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-nunito font-semibold">
      <Users size={9} /> Friend
    </span>
  );
  if (item.is_community) return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-green-100 text-green-700 font-nunito font-semibold">
      <Users size={9} /> Community
    </span>
  );
  return (
    <span className="inline-flex items-center gap-0.5 text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 font-nunito">
      <Navigation size={9} /> Public
    </span>
  );
}

function ProjectCard({ item }) {
  const borderClass = item.is_friend
    ? 'border-2 border-orange-400 hover:shadow-md'
    : item.is_community
      ? 'border border-green-200/70 hover:shadow-md'
      : 'border border-white/60 hover:shadow-md';
  return (
    <EntityTile
      testId={`feed-project-${item.id}`}
      borderClass={borderClass}
      header={<>
        <Link to={`/projects/${item.id}`} className="flex-1 min-w-0 hover:text-[#004B00] transition-colors">
          <h3 className="font-poppins font-semibold text-gray-900 text-sm line-clamp-2 leading-snug">{item.title}</h3>
        </Link>
        <SourceBadge item={item} />
      </>}
      photo={item.photo || item.owner_photo}
      placeholderIcon={<Wrench size={26} className="text-stone-300"/>}
      body={<>
        {item.description ? (
          <p className="text-xs text-gray-500 font-nunito line-clamp-2 leading-snug">{item.description}</p>
        ) : (
          <p className="text-xs text-gray-300 font-nunito italic">No description</p>
        )}
        {((item.categories?.length > 0) || (item.tools_needed?.length > 0)) && (
          <div className="flex flex-wrap gap-1">
            {item.categories?.slice(0, 2).map(c => (
              <span key={c} className="bg-green-50 text-[#004B00] border border-green-100 text-[10px] px-2 py-0.5 rounded-full font-nunito whitespace-nowrap">{c}</span>
            ))}
            {item.tools_needed?.slice(0, 2).map(t => (
              <span key={t} className="bg-stone-100 text-gray-600 text-[10px] px-2 py-0.5 rounded-full font-nunito whitespace-nowrap">{t}</span>
            ))}
            {((item.categories?.length || 0) + (item.tools_needed?.length || 0)) > 4 && (
              <span className="text-[10px] text-gray-400 font-nunito self-center">+{(item.categories?.length || 0) + (item.tools_needed?.length || 0) - 4}</span>
            )}
          </div>
        )}
        <div className="mt-auto pt-1"><OwnerRow item={item} /></div>
      </>}
      footer={<Link to={`/projects/${item.id}`} data-testid={`view-project-${item.id}`} className="flex-1 text-center text-xs bg-[#004B00] text-white py-2 rounded-lg hover:bg-[#003800] font-nunito font-semibold transition-all flex items-center justify-center gap-1"><ArrowRight size={11} /> View Project</Link>}
    />
  );
}

function GiveawayCard({ item, onClaim, claiming, myClaims = [] }) {
  const timeLeft = formatTimeRemaining(item.expires_at);
  const isUrgent = item.expires_at && (new Date(item.expires_at) - new Date()) < 86400000;
  const alreadyClaimed = myClaims.some(
    c => c.item_id === item.id && ['pending', 'approved'].includes(c.status)
  );
  // Match the source-derived border palette from /giveaways so the same item
  // looks identical on both pages.
  const borderClass = item.is_friend
    ? 'border-orange-200/70 hover:shadow-md'
    : item.is_community
      ? 'border-green-200/70 hover:shadow-md'
      : 'border-pink-100/70 hover:shadow-md';

  return (
    <EntityTile
      testId={`feed-giveaway-${item.id}`}
      borderClass={borderClass}
      header={<>
        <Link to={`/items/${item.id}`} className="flex-1 min-w-0 hover:text-pink-600 transition-colors">
          <h3 className="font-poppins font-semibold text-gray-900 text-sm line-clamp-2 leading-snug">{item.title}</h3>
        </Link>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className="bg-gradient-to-r from-pink-500 to-rose-500 text-white text-[10px] px-2 py-0.5 rounded-full font-nunito font-bold whitespace-nowrap">FREE</span>
          <SourceBadge item={item} />
        </div>
      </>}
      photo={item.photo}
      placeholderIcon={<Package size={26} className="text-stone-300"/>}
      body={<>
        {item.description ? (
          <p className="text-xs text-gray-500 font-nunito line-clamp-2 leading-snug">{item.description}</p>
        ) : (
          <p className="text-xs text-gray-300 font-nunito italic">No description</p>
        )}
        <div className="flex flex-wrap items-center gap-1">
          {item.category && (
            <span className={`text-[10px] px-2 py-0.5 rounded-full font-nunito whitespace-nowrap ${categoryColors[item.category] || 'bg-stone-100 text-stone-600'}`}>
              {item.category}
            </span>
          )}
        </div>
        {timeLeft && (
          <div className={`text-[10px] font-nunito flex items-center gap-1 ${isUrgent ? 'text-red-500' : 'text-amber-600'}`}>
            <Clock size={10}/> {timeLeft}
          </div>
        )}
        <div className="mt-auto pt-1"><OwnerRow item={item} /></div>
      </>}
      footer={
        alreadyClaimed ? (
          <div data-testid={`claim-sent-${item.id}`} className="flex-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs py-2 rounded-lg font-nunito font-semibold flex items-center justify-center gap-1">
            <Clock size={11}/> Request Sent
          </div>
        ) : (
          <button
            onClick={() => onClaim(item)}
            disabled={claiming === item.id}
            data-testid={`claim-${item.id}`}
            className="flex-1 bg-gradient-to-r from-pink-500 to-rose-500 hover:from-pink-600 hover:to-rose-600 text-white text-xs py-2 rounded-lg font-nunito font-semibold transition-all disabled:opacity-50 flex items-center justify-center gap-1 shadow-sm"
          >
            <Gift size={11}/> {claiming === item.id ? 'Claiming...' : 'Claim Now'}
          </button>
        )
      }
    />
  );
}

// ─── Section Wrapper ──────────────────────────────────────────────────────────

const SHOW_LIMIT = 8;

function FeedSection({ title, icon: Icon, accentColor, items, emptyMsg, emptyAction, children, renderCard, filterContent }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, SHOW_LIMIT);
  const friendCount = items.filter(i => i.is_friend).length;
  const communityCount = items.filter(i => i.is_community && !i.is_friend).length;

  return (
    <div className="mb-10">
      {/* Section Header */}
      <div className="glass-header flex items-center justify-between mb-4 flex-wrap gap-2 px-4 py-3 shadow-sm">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accentColor.bg}`}>
            <Icon size={18} className={accentColor.icon} />
          </div>
          <div>
            <h2 className="text-lg font-poppins font-semibold text-gray-900">{title}</h2>
            <div className="flex items-center gap-2 flex-wrap mt-0.5">
              {friendCount > 0 && (
                <span className="text-xs text-orange-600 font-nunito flex items-center gap-0.5">
                  <Users size={9} /> {friendCount} from friends
                </span>
              )}
              {communityCount > 0 && (
                <span className="text-xs text-green-700 font-nunito flex items-center gap-0.5">
                  <Users size={9} /> {communityCount} from community
                </span>
              )}
              {items.length > 0 && (
                <span className="text-xs text-gray-400 font-nunito">{items.length} total</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {filterContent}
          {children}
        </div>
      </div>

      {items.length === 0 ? (
        <div className="glass-card rounded-2xl border border-white/50 p-8 text-center">
          <p className="text-gray-400 font-nunito text-sm">{emptyMsg}</p>
          {emptyAction}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {visible.map(item => renderCard(item))}
          </div>
          {items.length > SHOW_LIMIT && (
            <button
              onClick={() => setExpanded(e => !e)}
              className="mt-4 w-full flex items-center justify-center gap-1.5 text-sm text-gray-500 hover:text-[#004B00] font-nunito font-semibold py-2.5 border border-white/50 rounded-xl glass-card hover:border-green-200/70 transition-all"
            >
              {expanded ? <><ChevronUp size={15} /> Show less</> : <><ChevronDown size={15} /> Show {items.length - SHOW_LIMIT} more</>}
            </button>
          )}
        </>
      )}
    </div>
  );
}

// ─── Main Page ─────────────────────────────────────────────────────────────────

export default function FeedPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [feedItems, setFeedItems] = useState([]);
  const [myClaims, setMyClaims] = useState([]);
  const [loading, setLoading] = useState(true);
  const [borrowItem, setBorrowItem] = useState(null);
  const [claiming, setClaiming] = useState(null);
  const [claimModal, setClaimModal] = useState(null);
  const [zipRank, setZipRank] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  useEffect(() => {
    // Show the intro tour to users who haven't seen it yet. The
    // `onboarding_completed` flag is stamped by the backend once the modal
    // is dismissed, so it never re-appears.
    if (user && user.onboarding_completed === false) {
      setShowOnboarding(true);
    }
  }, [user]);

  useEffect(() => {
    // Onboarding CTA deep-link: /feed?add=item opens the Add Item form
    // immediately. Used by the last step of OnboardingModal so brand-new
    // users go straight from tour → list-an-item without hunting for the
    // "+ Item" button.
    const params = new URLSearchParams(location.search);
    if (params.get('add') === 'item') {
      setShowAddItem(true);
      params.delete('add');
      const qs = params.toString();
      navigate({ pathname: location.pathname, search: qs ? `?${qs}` : '' }, { replace: true });
    }
  }, [location.search, location.pathname, navigate]);

  useEffect(() => {
    Promise.all([
      api.get('/feed/recommended').catch(() => ({ data: [] })),
      api.get('/my-claim-requests').catch(() => ({ data: [] })),
      api.get('/users/zip-rank').catch(() => ({ data: null })),
    ]).then(([feedRes, claimsRes, rankRes]) => {
      // Kick off the LCP image fetch *before* React commits the feed tree.
      // The active tab defaults to 'projects', so the first visible photo is
      // the LCP. We also warm the top image of each other tab so a quick
      // tab-switch feels instant. `fetchpriority=high` on the preload beats
      // any lazy images already scheduled by the browser.
      preloadHeroImages(feedRes.data);
      setFeedItems(feedRes.data);
      setMyClaims(claimsRes.data);
      setZipRank(rankRes.data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  const handleClaim = (item) => {
    setClaimModal(item);
  };

  const handleStartProject = (item) => {
    navigate(`/projects?tool=${encodeURIComponent(item.title)}&from=${encodeURIComponent(item.owner_name)}`);
  };

  // Split and sort each section
  const projects = feedItems.filter(i => i.type === 'project').sort(sectionSort);
  const tools = feedItems.filter(i => i.type === 'item').sort(sectionSort);
  const giveaways = feedItems.filter(i => i.type === 'giveaway').sort(sectionSort);

  const [activeTab, setActiveTab] = useState('tools');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null = use feedItems
  const searchTimerRef = useRef(null);

  // Clear search when switching tabs
  const handleTabSwitch = (tabId) => {
    setActiveTab(tabId);
    setSearchQuery('');
  };

  // Debounced server-side search — fires 350 ms after the user stops typing.
  // When searchQuery is empty, restores the original feed (searchResults = null).
  useEffect(() => {
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    if (!searchQuery.trim()) {
      setSearchResults(null);
      setSearchLoading(false);
      return;
    }
    setSearchLoading(true);
    const typeMap = { tools: 'item', projects: 'project', giveaways: 'giveaway' };
    searchTimerRef.current = setTimeout(async () => {
      try {
        const res = await api.get('/feed/search', {
          params: { q: searchQuery.trim(), type: typeMap[activeTab] || 'all' },
        });
        setSearchResults(res.data);
      } catch {
        setSearchResults([]);
      } finally {
        setSearchLoading(false);
      }
    }, 350);
    return () => { if (searchTimerRef.current) clearTimeout(searchTimerRef.current); };
  }, [searchQuery, activeTab]);

  const [toolCatFilter, setToolCatFilter] = useState(null);
  // When a search is active, use those results; otherwise use the full feed.
  const effectiveTools = searchResults !== null
    ? searchResults.filter(i => i.type === 'item')
    : tools;
  const effectiveProjects = searchResults !== null
    ? searchResults.filter(i => i.type === 'project')
    : projects;
  const effectiveGiveaways = searchResults !== null
    ? searchResults.filter(i => i.type === 'giveaway')
    : giveaways;

  const toolCategories = [...new Set(effectiveTools.map(i => i.category).filter(Boolean))].sort();
  const filteredTools = (toolCatFilter ? effectiveTools.filter(i => i.category === toolCatFilter) : effectiveTools);

  const [projectCatFilter, setProjectCatFilter] = useState(null);
  const projectCategories = [...new Set(effectiveProjects.flatMap(i => i.categories || []).filter(Boolean))].sort();
  const filteredProjects = (projectCatFilter ? effectiveProjects.filter(i => (i.categories || []).includes(projectCatFilter)) : effectiveProjects);

  const filteredGiveaways = effectiveGiveaways;

  const LoadingGrid = () => (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {[1,2,3,4].map(i => <div key={i} className="bg-white rounded-xl h-60 animate-pulse border border-stone-100" />)}
    </div>
  );

  return (
    <>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8 flex items-center justify-between flex-wrap gap-4 glass-header px-6 py-5 shadow-sm">
          <div>
            <h1 className="text-3xl font-poppins font-semibold text-gray-900">
              Hello, {user?.first_name || user?.name?.split(' ')[0]}!
            </h1>
            <p className="text-gray-500 font-nunito mt-1">
              Discover items and projects from friends, your community, and neighbors
            </p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => setShowAddProject(true)}
              data-testid="quick-add-project"
              className="bg-[#004B00] hover:bg-[#003800] text-white px-5 py-2.5 rounded-full font-nunito font-semibold text-sm transition-all flex items-center gap-2 active:scale-95 shadow-sm"
            >
              <Plus size={16} /> Project
            </button>
            <button
              onClick={() => setShowAddItem(true)}
              data-testid="quick-add-item"
              className="bg-[#004B00] hover:bg-[#003800] text-white px-5 py-2.5 rounded-full font-nunito font-semibold text-sm transition-all flex items-center gap-2 active:scale-95 shadow-sm"
            >
              <Plus size={16} /> Item
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Neighborli Progress', custom: 'status' },
            { label: 'Borrower Score', value: user?.rating_count > 0 ? `${user?.borrower_score?.toFixed(1)}/5` : 'No ratings yet', color: 'text-amber-600' },
            { label: 'Points Earned', value: user?.loan_count || 0, color: 'text-blue-600' },
            { label: 'Zip Code Rank', custom: 'rank' },
          ].map(({ label, value, color, custom }) => (
            <div key={label} className="glass-card rounded-xl p-4 border border-white/50 shadow-sm">
              <div className="text-xs text-gray-500 font-nunito uppercase tracking-wide mb-1">{label}</div>
              {custom === 'status' ? (
                <StatusProgress statusProgress={user?.status_progress} compact />
              ) : custom === 'rank' ? (
                zipRank?.rank ? (
                  <div>
                    <span className="text-xl font-poppins font-bold text-[#004B00]">#{zipRank.rank}</span>
                    <span className="text-sm text-gray-400 font-nunito ml-1">of {zipRank.total}</span>
                    <div className="text-xs text-gray-400 font-nunito mt-0.5">in {zipRank.zip_code}</div>
                  </div>
                ) : (
                  <div className="text-sm text-gray-400 font-nunito">Add zip code<br/>to see rank</div>
                )
              ) : (
                <div className={`text-xl font-poppins font-bold ${color}`}>{value}</div>
              )}
            </div>
          ))}
        </div>

        {/* Priority Legend + Search */}
        <div className="flex items-center gap-4 mb-6 flex-wrap">
          <span className="text-sm font-poppins font-semibold text-gray-700">Priority:</span>
          <div className="flex items-center gap-3 flex-wrap">
            <span className="flex items-center gap-1.5 text-xs font-nunito font-semibold text-orange-700 bg-orange-50 px-3 py-1 rounded-full border border-orange-100">
              <Users size={11} /> Friends first
            </span>
            <span className="text-gray-300 text-sm">→</span>
            <span className="flex items-center gap-1.5 text-xs font-nunito font-semibold text-green-700 bg-green-50 px-3 py-1 rounded-full border border-green-100">
              <Users size={11} /> Community
            </span>
            <span className="text-gray-300 text-sm">→</span>
            <span className="flex items-center gap-1.5 text-xs font-nunito font-semibold text-gray-600 bg-stone-50 px-3 py-1 rounded-full border border-stone-200">
              <Navigation size={11} /> Public by distance
            </span>
          </div>
          {!loading && feedItems.length > 0 && (
            <div className="relative ml-auto flex-shrink-0 w-64 sm:w-72" data-testid="feed-search-bar">
              {searchLoading
                ? <Loader2 size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#004B00] animate-spin pointer-events-none" />
                : <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              }
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder={
                  activeTab === 'projects' ? 'Search projects…'
                  : activeTab === 'tools' ? 'Search tools…'
                  : 'Search giveaways…'
                }
                data-testid="feed-search-input"
                className="w-full pl-9 pr-8 py-1.5 rounded-full glass-card border border-white/50 text-sm font-nunito text-gray-800 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[#004B00]/20 focus:border-[#004B00]/30 transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  data-testid="feed-search-clear"
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full hover:bg-stone-100 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X size={14} />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Tab Bar */}
        {!loading && (
          <div className="glass-card rounded-2xl border border-white/50 shadow-sm p-1.5 mb-6 flex gap-1">
            {[
              {
                id: 'tools', label: 'Tools & Items', icon: Package,
                count: filteredTools.length,
                activeClass: 'bg-blue-600 text-white shadow-md',
                inactiveClass: 'text-gray-600 hover:bg-blue-50 hover:text-blue-600',
                badgeCls: 'bg-blue-700/30',
              },
              {
                id: 'projects', label: 'Projects', icon: FolderOpen,
                count: filteredProjects.length,
                activeClass: 'bg-[#004B00] text-white shadow-md',
                inactiveClass: 'text-gray-600 hover:bg-green-50 hover:text-[#004B00]',
                badgeCls: 'bg-green-700/30',
              },
              {
                id: 'giveaways', label: 'Free Giveaways', icon: Gift,
                count: filteredGiveaways.length,
                activeClass: 'bg-gradient-to-r from-pink-500 to-rose-500 text-white shadow-md',
                inactiveClass: 'text-gray-600 hover:bg-pink-50 hover:text-pink-500',
                badgeCls: 'bg-pink-700/30',
              },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => handleTabSwitch(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl font-nunito font-semibold text-sm transition-all duration-200 ${
                  activeTab === tab.id ? tab.activeClass : tab.inactiveClass
                }`}
              >
                <tab.icon size={15} />
                <span className="hidden sm:inline">{tab.label}</span>
                {tab.count > 0 && (
                  <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                    activeTab === tab.id ? tab.badgeCls : 'bg-stone-100 text-gray-500'
                  }`}>
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>
        )}

        {loading ? (
          <div>
            <div className="h-8 w-40 bg-stone-100 rounded-lg animate-pulse mb-4" />
            <LoadingGrid />
          </div>
        ) : feedItems.length === 0 ? (
          <div className="glass-card rounded-2xl border border-white/50 p-16 text-center">
            <div className="w-16 h-16 bg-[#004B00]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Package className="text-[#004B00]" size={28} />
            </div>
            <h3 className="text-xl font-poppins font-semibold text-gray-900 mb-2">Your feed is empty</h3>
            <p className="text-gray-500 font-nunito mb-6 max-w-md mx-auto">
              Add friends, join communities, and add items to your library to see personalized recommendations.
            </p>
            <div className="flex gap-3 justify-center">
              <Link to="/friends" className="bg-[#004B00] text-white px-6 py-3 rounded-full font-nunito font-semibold hover:bg-[#003800] transition-all">
                Find Friends
              </Link>
              <Link to="/communities" className="border-2 border-[#004B00] text-[#004B00] px-6 py-3 rounded-full font-nunito font-semibold hover:bg-green-50 transition-all">
                Join Communities
              </Link>
            </div>
          </div>
        ) : (
          <>
            {activeTab === 'tools' && (
              <FeedSection
                title="Tools & Items"
                icon={Package}
                accentColor={{ bg: 'bg-blue-50', icon: 'text-blue-600' }}
                items={filteredTools}
                emptyMsg={searchQuery ? `No tools matching "${searchQuery}".` : toolCatFilter ? `No tools found in "${toolCatFilter}".` : "No tools or items available in your network yet."}
                emptyAction={
                  !toolCatFilter && <button onClick={() => setShowAddItem(true)} className="mt-3 text-[#004B00] hover:underline text-sm font-nunito font-semibold">Add your first item →</button>
                }
                filterContent={<CategoryFilterDropdown categories={toolCategories} value={toolCatFilter} onChange={setToolCatFilter} />}
                renderCard={(item) => (
                  <ToolCard
                    key={item.id}
                    item={item}
                    onBorrow={setBorrowItem}
                    onStartProject={handleStartProject}
                  />
                )}
              >
                <Link to="/items" className="text-sm text-[#004B00] font-nunito font-semibold hover:underline flex items-center gap-1">
                  My Library <ArrowRight size={13} />
                </Link>
              </FeedSection>
            )}

            {activeTab === 'projects' && (
              <FeedSection
                title="Projects"
                icon={FolderOpen}
                accentColor={{ bg: 'bg-green-50', icon: 'text-[#004B00]' }}
                items={filteredProjects}
                emptyMsg={searchQuery ? `No projects matching "${searchQuery}".` : projectCatFilter ? `No projects found in "${projectCatFilter}".` : "No active projects from your network yet."}
                emptyAction={
                  !projectCatFilter && <Link to="/projects" className="mt-3 inline-block text-[#004B00] hover:underline text-sm font-nunito font-semibold">Create a project →</Link>
                }
                filterContent={<CategoryFilterDropdown categories={projectCategories} value={projectCatFilter} onChange={setProjectCatFilter} />}
                renderCard={(item) => <ProjectCard key={item.id} item={item} />}
              >
                <Link to="/projects" className="text-sm text-[#004B00] font-nunito font-semibold hover:underline flex items-center gap-1">
                  My Projects <ArrowRight size={13} />
                </Link>
              </FeedSection>
            )}

            {activeTab === 'giveaways' && (
              <FeedSection
                title="Free Giveaways"
                icon={Gift}
                accentColor={{ bg: 'bg-pink-50', icon: 'text-pink-500' }}
                items={filteredGiveaways}
                emptyMsg={searchQuery ? `No giveaways matching "${searchQuery}".` : "No free giveaways available right now."}
                emptyAction={
                  <Link to="/giveaways" className="mt-3 inline-block text-pink-600 hover:underline text-sm font-nunito font-semibold">
                    Browse all giveaways →
                  </Link>
                }
                renderCard={(item) => (
                  <GiveawayCard
                    key={item.id}
                    item={item}
                    onClaim={handleClaim}
                    claiming={claiming}
                    myClaims={myClaims}
                  />
                )}
              >
                <Link to="/giveaways" className="text-sm text-pink-600 font-nunito font-semibold hover:underline flex items-center gap-1">
                  All Giveaways <ArrowRight size={13} />
                </Link>
              </FeedSection>
            )}
          </>
        )}
      </div>

      {borrowItem && (
        <BorrowModal
          item={borrowItem}
          onClose={() => setBorrowItem(null)}
          onSuccess={() => setBorrowItem(null)}
        />
      )}

      {claimModal && (
        <ClaimRequestModal
          item={claimModal}
          onClose={() => setClaimModal(null)}
          onSuccess={(itemId) => {
            setFeedItems(prev => prev.filter(i => i.id !== itemId));
            setMyClaims(prev => [...prev, { id: `temp-${itemId}`, item_id: itemId, status: 'pending' }]);
            setClaimModal(null);
          }}
        />
      )}
      {showAddItem && (
        <ItemFormModal onClose={() => setShowAddItem(false)} onSuccess={() => setShowAddItem(false)} />
      )}
      {showAddProject && (
        <ProjectFormModal
          isOpen={showAddProject}
          onClose={() => setShowAddProject(false)}
          onSave={() => setShowAddProject(false)}
        />
      )}
      {showOnboarding && (
        <OnboardingModal onClose={() => setShowOnboarding(false)} />
      )}
    </>
  );
}
